import React, { useState, useEffect } from 'react';
import { CUFGenerationChart, AnomalyFlag, MetricTogglePanel } from '../components/BlockDiagnostics';
import { cufGenerationService, CUFAnomalyData } from '../services/cufGenerationService';
import { Card } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

export const CUFGenerationDashboard: React.FC = () => {
  const [selectedMetrics, setSelectedMetrics] = useState(['generation', 'cuf']);
  const [anomalies, setAnomalies] = useState<CUFAnomalyData[]>([]);
  const [loading, setLoading] = useState(true);
  const blockId = 'BLOCK-4C-DI';
  const dateRange = '2024-10-01_to_2024-10-10';

  useEffect(() => {
    const fetchAnomalies = async () => {
      try {
        setLoading(true);
        const result = await cufGenerationService.getCUFAnomalies(blockId);
        setAnomalies(result);
      } catch (err) {
        console.error('Error fetching anomalies:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnomalies();
  }, [blockId]);

  const handleMetricToggle = (metric: string) => {
    setSelectedMetrics(prev => 
      prev.includes(metric) 
        ? prev.filter(m => m !== metric)
        : [...prev, metric]
    );
  };

  const exportData = (format: 'csv' | 'png') => {
    // Placeholder for export functionality
    console.log(`Exporting data as ${format}`);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CUF vs Generation Analysis</h1>
          <p className="text-gray-600 mt-1">
            Block: <span className="font-medium">{blockId}</span> | 
            Period: <span className="font-medium">Oct 1-10, 2024</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => exportData('csv')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Export CSV
          </button>
          <button
            onClick={() => exportData('png')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Export PNG
          </button>
        </div>
      </div>

      {/* Metric Toggle Panel */}
      <MetricTogglePanel
        selectedMetrics={selectedMetrics}
        onMetricToggle={handleMetricToggle}
      />

      {/* Main Chart */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Daily Generation & CUF Correlation
          </h2>
          <div className="text-sm text-gray-500">
            Hover over data points for details
          </div>
        </div>
        <CUFGenerationChart blockId={blockId} dateRange={dateRange} />
      </Card>

      {/* Anomalies Section */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Detected Anomalies
        </h2>
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : anomalies.length > 0 ? (
          <div className="space-y-3">
            {anomalies.map((anomaly, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <AnomalyFlag type={anomaly.anomaly_type} />
                  <div>
                    <p className="font-medium text-gray-900">
                      {new Date(anomaly.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                    <p className="text-sm text-gray-600">
                      Generation: {anomaly.generation_kwh.toFixed(1)} kWh | 
                      CUF: {anomaly.cuf.toFixed(1)}%
                    </p>
                  </div>
                </div>
                <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  View Details
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No anomalies detected in the selected period
          </div>
        )}
      </Card>

      {/* Key Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-4">
          <h3 className="font-semibold text-gray-900 mb-2">CUF Drift Analysis</h3>
          <p className="text-2xl font-bold text-blue-600">-2.3%</p>
          <p className="text-sm text-gray-600">vs previous period</p>
        </Card>
        
        <Card className="p-4">
          <h3 className="font-semibold text-gray-900 mb-2">Generation Efficiency</h3>
          <p className="text-2xl font-bold text-green-600">94.2%</p>
          <p className="text-sm text-gray-600">of expected output</p>
        </Card>
        
        <Card className="p-4">
          <h3 className="font-semibold text-gray-900 mb-2">Anomaly Rate</h3>
          <p className="text-2xl font-bold text-orange-600">20%</p>
          <p className="text-sm text-gray-600">2 of 10 days</p>
        </Card>
      </div>
    </div>
  );
};